/**
 * Shared wheelchair type-name constants, SWO display logic that splits a
 * "Bariatric Manual Wheelchair" line item into a Heavy Duty (K0006) or Extra
 * Heavy Duty (K0007) tier based on patient weight, and the bariatric
 * weight-confirmation check the medical-necessity graph names as a source.
 * Presentation-only where noted — the underlying form item and persisted line
 * item are unchanged.
 */

import { WheelchairsAndScootersDeviceType } from "../__generated__/graphql";

export const BARIATRIC_MANUAL_WHEELCHAIR_NAME = "Bariatric Manual Wheelchair";
export const BARIATRIC_TRANSPORT_WHEELCHAIR_NAME =
  "Bariatric Transport Wheelchair";
export const TRANSPORT_WHEELCHAIR_NAME = "Transport Wheelchair";
export const HEAVY_DUTY_MANUAL_WHEELCHAIR_NAME = "Heavy Duty Manual Wheelchair";
export const EXTRA_HEAVY_DUTY_MANUAL_WHEELCHAIR_NAME =
  "Extra Heavy Duty Manual Wheelchair";
export const ORDER_CUSHION_ONLY_NAME = "Order Cushion Only";

export const HEAVY_DUTY_HCPCS_CODE = "K0006";
export const EXTRA_HEAVY_DUTY_HCPCS_CODE = "K0007";

// Mirrors the grouping in services/orders/read/pn_summary_completeness.py.
export const BARIATRIC_WHEELCHAIR_TYPES = [
  BARIATRIC_MANUAL_WHEELCHAIR_NAME,
  BARIATRIC_TRANSPORT_WHEELCHAIR_NAME,
];
export const TRANSPORT_WHEELCHAIR_TYPES = [
  TRANSPORT_WHEELCHAIR_NAME,
  BARIATRIC_TRANSPORT_WHEELCHAIR_NAME,
];

// Patients with weight > 300 lbs require Extra Heavy Duty (K0007); 251–300 lbs
// is Heavy Duty (K0006).
export const HEAVY_DUTY_WEIGHT_THRESHOLD_LBS = 250;
export const EXTRA_HEAVY_DUTY_WEIGHT_THRESHOLD_LBS = 300;

export const BARIATRIC_WEIGHT_THRESHOLDS_LBS: Partial<
  Record<WheelchairsAndScootersDeviceType, number>
> = {
  [WheelchairsAndScootersDeviceType.BariatricManual]:
    HEAVY_DUTY_WEIGHT_THRESHOLD_LBS,
};

/** True when a bariatric device is picked for a patient who doesn't clearly
 * need it by weight alone. Backs the `needs_bariatric_weight_confirmation`
 * source in the medical-necessity graph (see validate/wheelchairs_and_scooters.py) */
export const needsBariatricWeightConfirmation = (
  deviceType: WheelchairsAndScootersDeviceType | null,
  patientWeightLbs: number | null | undefined,
): boolean => {
  if (deviceType == null || patientWeightLbs == null) return false;
  const threshold = BARIATRIC_WEIGHT_THRESHOLDS_LBS[deviceType];
  return threshold != null && patientWeightLbs <= threshold;
};

type BariatricWheelchairSwoOverride = {
  name: string;
  hcpcsCode: string;
};

/**
 * Returns the SWO display override for a Bariatric Manual Wheelchair line
 * item, or null when no override applies (non-bariatric, or weight unknown).
 *
 * @param formItemName - The catalog/line-item name of the wheelchair.
 * @param patientWeight - The patient's weight in lbs from the wheelchair plan
 *   of care. Null/undefined leaves the line item as plain "Bariatric Manual
 *   Wheelchair".
 */
export const getBariatricWheelchairItemOverride = (
  formItemName: string | null | undefined,
  patientWeight: number | null | undefined,
): BariatricWheelchairSwoOverride | null => {
  if (formItemName !== BARIATRIC_MANUAL_WHEELCHAIR_NAME) return null;
  if (patientWeight == null) return null;

  if (patientWeight > EXTRA_HEAVY_DUTY_WEIGHT_THRESHOLD_LBS) {
    return {
      name: EXTRA_HEAVY_DUTY_MANUAL_WHEELCHAIR_NAME,
      hcpcsCode: EXTRA_HEAVY_DUTY_HCPCS_CODE,
    };
  }

  if (patientWeight > HEAVY_DUTY_WEIGHT_THRESHOLD_LBS) {
    return {
      name: HEAVY_DUTY_MANUAL_WHEELCHAIR_NAME,
      hcpcsCode: HEAVY_DUTY_HCPCS_CODE,
    };
  }

  return null;
};
